import * as Sentry from "@sentry/react";
import {ATTRIBUTE_CODE_COLOR, IS_GRAVIUR} from "../constants/common";
import BigNumber from "bignumber.js";
import history from "../routes/history";


export const GetFinalPrice = (item, values, selectedItem, force = true) => {
    if (item.product_type === 'simple') {
        const price = new BigNumber(item.product_price);
        return price.toFormat(2).toString();
    }

    const {product_simples} = item;
    // If Product In Cart
    if (selectedItem && !force) {
        return selectedItem.price;
    }

    const product = product_simples.find((product_simple) => {
        let find = true;
        for (
            let index = 0;
            index < product_simple.product_custom_attributes.length;
            index++
        ) {
            const element = product_simple.product_custom_attributes[index];
            if (element.value !== values[element.attribute_code]) {
                return false;
            }
        }

        return find;
    });

    if (!product) {
        Sentry.setTag("TYPE", "PRODUCT_NOT_FOUND");
        Sentry.captureException(new Error("PRODUCT_NOT_FOUND"));
        return;
    }

    if (values.is_graviur === IS_GRAVIUR) {
        // Gravur
        const gravurOption = item.product_custom_options.find(
            (custom_option) => custom_option.type === "field"
        );
        const price = new BigNumber(product.product_price).plus(
            new BigNumber(gravurOption.price)
        );
        return price.toFormat(2).toString();
    }

    const price = new BigNumber(product.product_price);
    return price.toFormat(2).toString();
};


export const GetLowestPrice = (item) => {
    const {product_simples} = item;

    return product_simples.reduce(function (a, b) {
        return a.product_price <= b.product_price ? a : b;
    }, {});
}

export const GetLowestColor = (item) => {
    const lowestPrice = GetLowestPrice(item);

    return lowestPrice.product_custom_attributes.find(
        (product_custom_attribute) =>
            product_custom_attribute.attribute_code === ATTRIBUTE_CODE_COLOR
    );
}

export const InitCartFormValue = (item, selectedItem) => {
    const {configurable_product_options} = item;

    const lowestPrice = GetLowestPrice(item);

    return configurable_product_options.reduce((obj, item) => {
        let lowestAttribute = lowestPrice.product_custom_attributes.find(
            (product_custom_attribute) =>
                product_custom_attribute.attribute_code === item.attribute_code
        );
        return Object.assign(obj, {
            [item.attribute_code]: selectedItem?.options[item.attribute_code]
                ? selectedItem?.options[item.attribute_code]
                : lowestAttribute?.value,
        });
    }, {})
}

export const HandleSuccessOrder = (globalActions) => {
    try {
        if (window.dataLayer && globalActions.getCheckout()?.totals?.base_grand_total) {
            window.dataLayer.push({
                event: 'calcuso-ai-payment-success',
                page_path: '/calcuso-ai-payment-success',
                page_title: 'AI | Product Payment Success',
                subtotal_incl_tax: globalActions.getCheckout().totals.base_grand_total
            });
        }
    } catch (e) {
        console.error("Error pushing to dataLayer:", e);
    }
    globalActions.initCheckout();
    globalActions.initCalcusoAI();
    globalActions.initFakeCart();

    history.push(`/checkout/share`);
}

export const IsAddItemToCart = (itemId, cartItem) => {
    let inCart = false;
    let cartValues = Object.values(cartItem);

    cartValues.map((item) => {
        if (item.id === itemId) {
            inCart = true;
        }
    });

    return inCart;
}

export

const findReferCode = function (url) {
    // Some social media platforms encode the URL, so we need to decode it first
    let decoded = decodeURIComponent(url);
    const regex = /refer_code=([A-Za-z0-9]+)/;
    const match = decoded.match(regex);

    if (match) {
        return match[1];
    }
}

/**
 * Set a cookie with the given name, value, and expiration days
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Number of days until expiration (default: 90)
 */
export const setCookie = (name, value, days = 90) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
}

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
export const getCookie = (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
}

/**
 * Delete a cookie by name
 * @param {string} name - Cookie name
 */
export const deleteCookie = (name) => {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
}

/**
 * Tracking parameters to check for marketing attribution
 */
const TRACKING_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gad_campaignid',
    'gclid',
    'fbclid',
    'msclkid',
    'ttclid'
];

/**
 * Brand campaign IDs that should not overwrite last_nondirect_touch if it already exists
 */
const CAMPAIGN_BRANDS = [
    // Add brand campaign IDs here if needed
    // Example: '1234567890'
    // Will have handle in server side
];

/**
 * Extract calcuso_attribution from URL and process it according to PHP logic
 */
export const setCalcusoAttributionCookie = () => {
    try {
        const timestamp = Math.floor(Date.now() / 1000);
        const url = window.location.href;
        
        // Get all URL params (from both query string and hash)
        const urlBeforeHash = url.split('#')[0];
        const urlParams = new URLSearchParams(urlBeforeHash.split('?')[1] || '');
        
        // Also check hash params
        if (url.includes('#')) {
            const hashPart = url.split('#')[1];
            if (hashPart && hashPart.includes('?')) {
                const hashParams = new URLSearchParams(hashPart.split('?')[1]);
                hashParams.forEach((value, key) => {
                    if (!urlParams.has(key)) {
                        urlParams.set(key, value);
                    }
                });
            }
        }
        
        // Check if calcuso_attribution is already in URL (legacy support)
        let calcusoAttribution = urlParams.get('calcuso_attribution');
        if (calcusoAttribution) {
            try {
                const parsed = JSON.parse(decodeURIComponent(calcusoAttribution));
                if (parsed && typeof parsed === 'object') {
                    setCookie('calcuso_attribution', JSON.stringify(parsed), 90);
                    return;
                }
            } catch (e) {
                // If parsing fails, continue with normal processing
            }
        }
        
        // Collect tracking parameters
        const incomingData = {};
        let isMarketingHit = false;
        
        TRACKING_PARAMS.forEach(param => {
            const value = urlParams.get(param);
            if (value && value !== '') {
                // HTML escape similar to htmlspecialchars in PHP
                incomingData[param] = value
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&#039;');
                isMarketingHit = true;
            }
        });
        
        // Get existing cookie data
        const existingCookie = getCookie('calcuso_attribution');
        let data = {};
        
        if (existingCookie && existingCookie !== '') {
            try {
                const decoded = JSON.parse(existingCookie);
                if (decoded && typeof decoded === 'object') {
                    data = decoded;
                }
            } catch (e) {
                // If parsing fails, start with empty data
            }
        }
        
        // Check if it's a brand campaign
        const gadCampaignId = incomingData['gad_campaignid'] || '';
        const isBrandCampaign = CAMPAIGN_BRANDS.includes(gadCampaignId);
        
        // Update last_touch
        if (isMarketingHit) {
            data['last_touch'] = {
                ...incomingData,
                type: 'marketing',
                timestamp: timestamp
            };
        } else {
            data['last_touch'] = {
                source: 'direct',
                timestamp: timestamp
            };
        }
        
        // Update last_nondirect_touch
        if (isMarketingHit) {
            if (!isBrandCampaign) {
                // Not a brand campaign -> always update
                data['last_nondirect_touch'] = {
                    ...incomingData,
                    timestamp: timestamp
                };
            } else {
                // Brand campaign -> only update if empty
                if (!data['last_nondirect_touch'] || Object.keys(data['last_nondirect_touch']).length === 0) {
                    data['last_nondirect_touch'] = {
                        ...incomingData,
                        timestamp: timestamp
                    };
                }
            }
        }
        
        // Update first_touch (only if empty)
        if (!data['first_touch'] || Object.keys(data['first_touch']).length === 0) {
            if (isMarketingHit) {
                data['first_touch'] = {
                    ...incomingData,
                    timestamp: timestamp,
                    type: 'marketing'
                };
            } else {
                data['first_touch'] = {
                    source: 'direct',
                    timestamp: timestamp
                };
            }
        }
        
        // Save to cookie with 90 days expiration
        setCookie('calcuso_attribution', JSON.stringify(data), 90);
    } catch (error) {
        console.error('Error setting calcuso_attribution cookie:', error);
    }
}


export const cleanCalcusoAttributionParamOnUrl = () => {
    try {
        const href = window.location.href;

        const [beforeHash, rawHash = ""] = href.split('#');

        // Process query params in the main URL
        const [baseUrl, queryString = ""] = beforeHash.split('?');
        const queryParams = new URLSearchParams(queryString);
        const hadMainParam = queryParams.has('calcuso_attribution');

        console.log('hadMainParam', queryParams);
        if (hadMainParam) {
            queryParams.delete('calcuso_attribution');
        }

        // Process query params inside the hash (if any), e.g., #/route?calcuso_attribution=...
        let newHash = rawHash;
        let hadHashParam = false;
        if (rawHash) {
            const [hashPath, hashQuery = ""] = rawHash.split('?');
            if (hashQuery) {
                const hashParams = new URLSearchParams(hashQuery);
                if (hashParams.has('calcuso_attribution')) {
                    hashParams.delete('calcuso_attribution');
                    hadHashParam = true;
                }
                const newHashQuery = hashParams.toString();
                newHash = newHashQuery ? `${hashPath}?${newHashQuery}` : hashPath;
            }
        }

        // Only update the URL if something changed
        if (hadMainParam || hadHashParam) {
            const newQuery = queryParams.toString();
            const rebuilt = newQuery ? `${baseUrl}?${newQuery}` : baseUrl;
            const finalUrl = newHash ? `${rebuilt}#${newHash}` : rebuilt;
            window.history.replaceState({}, document.title, finalUrl);
        }
    } catch (e) {
        console.error('Error cleaning calcuso_attribution from URL:', e);
    }
}